import React, {Fragment, FunctionComponent} from 'react';
import {ErrorMessage} from '../../common/error-message/ErrorMessage';
import {useTranslation} from "react-i18next";
import {PageTemplate} from "../../common/page-template/PageTemplate";
import {Box, Grid, Link} from "@mui/material";
import {Loader} from "../../common/loader/Loader";
import {useGetApiV1ProductQuery, useGetApiV1UserMeQuery} from "../../../api/GovernanceToolApi";
import {useParams} from "react-router-dom";
import {DateUtils} from '../../../book/DateUtils';
import {styled} from "@mui/material/styles";
import {UserRole, UserRoleUtils} from "../../../book/UserRoleUtils";

const LabelGrid = styled(Grid)(({theme}) => ({
    fontWeight: 'bolder',
    textAlign: 'right',
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
    },
}));

const SimpleValueLink = styled(Link)(() => ({
    whiteSpace: 'pre-line',
}));

const SimpleValueBox = styled(Box)(() => ({
    whiteSpace: 'pre-line',
}));

export const ProductDetails: FunctionComponent= () => {
    const {t} = useTranslation();
    const {productId} = useParams();
    const {data: productData, isLoading, error} = useGetApiV1ProductQuery({productId});
    const {data: userMe} = useGetApiV1UserMeQuery(undefined)

    const product = productData?.payload?.product;

    // Groups by category
    const productProperties = (product?.productProperties || []).reduce((agg, curr) => {
        if (curr.category && curr.value) {
            if (!agg.hasOwnProperty(curr.category)) {
                agg[curr.category] = [];
            }

            agg[curr.category] = [...agg[curr.category], {value: curr.value, type: curr.type ?? 0}];
        }

        return agg;
    }, {} as { [category: string]: { value: string, type: number }[] });

    return <PageTemplate pageTitle={`${product?.name}`}
                         pageSubTitle={`v. ${product?.version ?? ''} ${product?.isStableRelease ? "(stable)" : "(current)"}`}
                         showBackButton={true}>
        {isLoading && <Loader message={t("Loading product…")}/>}

        {error && <ErrorMessage
            message={t("An error occurred while loading product, please contact the administrator or try again")}/>}

        {product && <Grid container spacing={1}>
            <LabelGrid item xs={12} sm={4}>
                {product.isStableRelease ? t("Creation date") : t("Last update")}:
            </LabelGrid>
            <Grid item xs={12} sm={8}>
                {product.isStableRelease ? DateUtils.dateTimeFormat(product.inserted) : DateUtils.dateTimeFormat(product.updated)}
            </Grid>
            <LabelGrid item xs={12} sm={4}>
                {t("Creator")}:
            </LabelGrid>
            <Grid item xs={12} sm={8}>
                {product.updater}
            </Grid>

            {Object.entries(productProperties).map(([category, items]) => {
                const lowerCategory = category.toLowerCase();
                const key = `category-${lowerCategory}`;
                const valuesNode: React.ReactNode = (() => {
                    switch (lowerCategory) {
                        case 'repository root':
                            return items.map((item, i) => {
                                const type = item.type === 0 ? t("Property") : t("Metadata");
                                return <SimpleValueLink href={item.value}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={UserRoleUtils.ToRole(userMe?.payload?.role) === UserRole.Admin ? type : undefined}
                                                        key={`not-symbol-${i}`}>
                                    {item.value}
                                </SimpleValueLink>;
                            });
                        default:
                            return items.map((item, i) => {
                                const type = item.type === 0 ? t("Property") : t("Metadata");
                                return <SimpleValueBox
                                    title={UserRoleUtils.ToRole(userMe?.payload?.role) === UserRole.Admin ? type : undefined}
                                    key={`not-symbol-${i}`}>
                                    {item.value}
                                </SimpleValueBox>;
                            });
                    }
                })();

                return <Fragment key={key}>
                    <LabelGrid item xs={12} sm={4}>
                        {category}:
                    </LabelGrid>
                    <Grid item xs={12} sm={8}>
                        {valuesNode}
                    </Grid>
                </Fragment>;
            })}
        </Grid>}
    </PageTemplate>;
}